import { useMutation } from '@tanstack/react-query'
import Modal from 'components/modal'
import { useShowSnack } from 'components/snackbar'
import { useState } from 'react'
import Button from 'components/button'
import { Dialog, Input, SizableText, Text, View, XStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import { usePurchaserContext } from '../purchaserProvider'

interface Props {
    open: boolean
    onClose: () => void
    formType: string
    documentId?: string
    title?: string
    message?: string
}

const AbandonFormModal = ({
    open,
    onClose,
    formType,
    documentId,
    title = 'Delete Document',
    message = 'Are you sure you want to delete this document? This action cannot be undone.',
}: Props) => {
    const { purchaser, setPurchaser } = usePurchaserContext()
    const showSnack = useShowSnack()
    const trpc = useTRPC()
    const abandonFormMutation = useMutation(trpc.esign.abandonForm.mutationOptions())

    const [reason, setReason] = useState('')

    const handleClose = () => {
        setReason('')
        onClose()
    }

    const handleAbandon = async () => {
        console.log('handleAbandon')

        try {
            const { updatedPurchaser } = await abandonFormMutation.mutateAsync({
                purchaserId: purchaser.purchaserId,
                formType: (formType === 'generic' ? 'generic' : formType) as
                    | 'agentAppointmentForm'
                    | 'generic',
                formId: documentId,
                reason,
            })

            setPurchaser(updatedPurchaser)
            showSnack('Document deleted successfully', 'success')
            handleClose()
        } catch (error: any) {
            console.error('Error deleting document:', error)
            showSnack(error.message || 'Error deleting document', 'danger')
        }
    }

    return (
        <Modal
            open={open}
            onOpenChange={(open) => { if (!open) handleClose() }}
        >
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Description>
                <View>
                    <Text marginBottom="$4">{message}</Text>
                    <SizableText
                        fontSize="$2"
                        marginBottom="$2"
                    >
                        Reason
                    </SizableText>
                    <Input
                        height={44}
                        placeholder="Please provide a reason for deleting this document..."
                        value={reason}
                        onChange={(e: any) => setReason(e.nativeEvent?.text ?? e.target?.value ?? '')}
                        width="100%"
                    />
                </View>
            </Dialog.Description>
            <XStack gap="$2" flexDirection="row-reverse">
                <Button
                    disabled={!reason.trim()}
                    theme="red"
                    onPress={handleAbandon}
                >
                    Abandon
                </Button>
                <Button
                    variant="outlined"
                    onPress={handleClose}
                >
                    Cancel
                </Button>
            </XStack>
        </Modal>
    )
}

export default AbandonFormModal
